import { BehaviorSubject, merge, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  startWith,
  takeUntil,
  tap
} from 'rxjs/operators';

import { CdkScrollable } from '@angular/cdk/scrolling';
import { AsyncPipe, DOCUMENT, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { NavigationStart, Router, RouterModule } from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { CheckForUpdateService } from './check-for-update.service';
import { User } from './models/user';
import { NetworkConnectivityService } from './network-connectivity.service';
import { AuthenticationService } from './shared/';
import { BarcodeScannerDialogComponent } from './shared/barcode-scanner-dialog/dialog/dialog.component';
import { ItemDialogComponent } from './shared/dialogs/item-dialog/item-dialog.component';
import { NewTransactionDialogComponent } from './shared/dialogs/new-transaction-dialog/new-transaction-dialog.component';
import { TimeoutDialogComponent } from './shared/dialogs/timeout-dialog/timeout-dialog.component';
import { CustomerNamePipe } from './shared/pipes/customer-name.pipe';
import { ConfigService } from './shared/services/config.service';
import { CurrentLocationService } from './shared/services/current-location.service';
import { SearchService } from './shared/services/search.service';
import { UpdateDialogComponent } from './shared/update-dialog/update-dialog.component';

@Component({
  selector: 'hire-logic',
  templateUrl: './hirelogic.component.html',
  styleUrls: ['./hirelogic.component.scss'],
  providers: [CustomerNamePipe, Idle, CurrentLocationService],
  standalone: true,
  imports: [
    MatToolbarModule,
    RouterModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatIconModule,
    MatMenuModule,
    MatMenu,
    AsyncPipe,
    MatButtonModule,
    NgIf,
    CdkScrollable
  ]
})
export class HirelogicComponent implements OnInit, OnDestroy {
  title = 'HireLogic';

  loading: boolean;
  authenticated = false;
  currentUser: User;
  currentLocation$ = this.currentLocationService.currentLocation$;
  searchInput: UntypedFormControl;
  searchVisible = false;
  @ViewChild('searchInputElement', { static: false })
  searchInputElement: ElementRef<HTMLInputElement>;
  searchResults = new BehaviorSubject<any>(<any>[]);
  noSearchResults = new BehaviorSubject<boolean>(false);
  private ngOnUnsubscribe: Subject<void> = new Subject();
  timeoutDialog: MatDialogRef<TimeoutDialogComponent>;

  constructor(
    private route: Router,
    public dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private authenticationService: AuthenticationService,
    private customerNamePipe: CustomerNamePipe,
    private configService: ConfigService,
    private searchService: SearchService,
    private idle: Idle,
    private viewContainerRef: ViewContainerRef,
    private checkForUpdateService: CheckForUpdateService,
    private currentLocationService: CurrentLocationService,
    private networkConnectivityService: NetworkConnectivityService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.searchInput = new UntypedFormControl();
    this.searchInput.valueChanges
      .pipe(
        startWith(null),
        tap(() => this.noSearchResults.next(false)),
        debounceTime(500),
        distinctUntilChanged()
      )
      .subscribe((term) => this.getSearchResults(term));
    this.setUpIdleTimeout();
    this.networkConnectivityService.listenForNetworkConnectivityChanges();
    this.checkForUpdateService.checkForUpdate();
  }

  public ngOnInit() {
    this.setUpConfigSubscriptions();
    this.currentLocationService.checkLocation();
    this.route.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        this.searchVisible = false;
      });
  }

  private setUpConfigSubscriptions() {
    this.authenticationService.authenticated
      .pipe(takeUntil(this.ngOnUnsubscribe))
      .subscribe((authenticated) => {
        this.authenticated = authenticated;
      });
    this.configService.currentUser
      .pipe(takeUntil(this.ngOnUnsubscribe))
      .subscribe((user) => {
        if (user) {
          this.currentUser = user;
          this.setBodyClass();
        }
      });
    if (this.authenticationService.token) {
      this.authenticationService
        .getLoggedInUser()
        .pipe(takeUntil(this.ngOnUnsubscribe))
        .subscribe((response) => {
          if (response.status !== 200) {
            this.authenticationService.notAuthenticated();
          }
        });
    }
  }

  logOut(timeout = false) {
    this.authenticationService.notAuthenticated(timeout);
  }

  editLocation() {
    this.currentLocationService.editLocation();
  }

  getSearchResults(term) {
    if (!term) {
      this.searchResults.next([]);
      return;
    }
    return this.searchService.gloabalSearch(term).subscribe((response) => {
      let results = response;
      let res = [];
      this.noSearchResults.next(
        results['customers'].length === 0 &&
          results['dockets'].length === 0 &&
          results['categories'].length === 0 &&
          results['products'].length === 0 &&
          results['items'].length === 0 &&
          results['retailProducts'].length === 0 &&
          results['saleDockets'].length === 0 &&
          results['repairDockets'].length === 0 &&
          results['bookings'].length === 0
      );
      if (results && results['customers'] && results['customers'].length) {
        const customers = { label: 'Customers', items: [] };
        results.customers.forEach((item) => {
          customers.items.push({
            name: this.customerNamePipe.transform(item),
            subText: item.address_1,
            onSage: item.sage_customer_id ? true : false,
            id: item.id,
            type: 'Customers'
          });
        });
        res.push(customers);
      }
      if (results && results['dockets'] && results['dockets'].length) {
        const dockets = { label: 'Dockets', items: [] };
        results.dockets.forEach((item) => {
          dockets.items.push({
            name: this.customerNamePipe.transform(item.customer),
            id: item.id,
            type: 'Dockets'
          });
        });
        res.push(dockets);
      }
      if (results && results['categories'] && results['categories'].length) {
        const cats = { label: 'Categories', items: [] };
        cats.items = results['categories'].map((cat) => {
          return {
            id: cat.id,
            name: cat.name,
            type: 'Categories'
          };
        });
        res.push(cats);
      }
      if (results && results['products'] && results['products'].length) {
        const products = { label: 'Products', items: [] };
        results.products.forEach((item) => {
          products.items.push({
            name: item.name,
            id: item.id,
            type: 'Products'
          });
        });
        res.push(products);
      }
      if (results && results['items'] && results['items'].length) {
        const items = { label: 'Items', items: [] };
        results.items.forEach((item) => {
          items.items.push({
            name: item.code,
            id: item.id,
            product_id: item.product_id,
            type: 'Items'
          });
        });
        res.push(items);
      }
      if (
        results &&
        results['retailProducts'] &&
        results['retailProducts'].length
      ) {
        const items = { label: 'Retail Products', items: [] };
        results.retailProducts.forEach((item) => {
          items.items.push({
            name: item.name,
            subText: item.formatted_identifiers,
            id: item.id,
            type: 'Retail Products'
          });
        });
        res.push(items);
      }
      if (results && results['saleDockets'] && results['saleDockets'].length) {
        const items = results.saleDockets.map((item) => {
          return {
            name: this.customerNamePipe.transform(item.customer),
            subText: `Sales Invoice ${item.invoice_number ?? ''}`.trim(),
            id: item.id,
            type: 'sale'
          };
        });
        res.push({ label: 'Sales', items });
      }
      if (
        results &&
        results['repairDockets'] &&
        results['repairDockets'].length
      ) {
        const items = results.repairDockets.map((item) => {
          const title =
            (item.invoice_number ? '' : `Draft `) +
            `Repair Invoice ${item.invoice_number}`.trim();
          return {
            name: this.customerNamePipe.transform(item.customer),
            subText: title,
            id: item.id,
            type: 'repair'
          };
        });
        res.push({ label: 'Repairs', items });
      }
      if (results && results['bookings']?.length) {
        const items = results.bookings.map((item) => {
          const title = `Booking ${item.id}`;
          return {
            name: this.customerNamePipe.transform(item.customer),
            subText: title,
            id: item.id,
            type: 'booking'
          };
        });
        res.push({ label: 'Bookings', items });
      }
      this.searchResults.next(res);
    });
  }

  goToItem(id, type, event) {
    this.searchVisible = false;
    if (event.source && event.source.stopPropogation) {
      event.source.stopPropagation();
    }
    switch (type) {
      case 'Categories':
        this.route.navigate(['/product/category', id]);
        break;
      case 'sale':
        this.route.navigate(['/sale-docket', id]);
        break;
      case 'repair':
        this.route.navigate(['/repair-docket', id]);
        break;
      case 'Customers':
        this.route.navigate(['/customer', id]);
        break;
      case 'Dockets':
        this.route.navigate(['/docket', id]);
        break;
      case 'booking':
        this.route.navigate(['/bookings/view', id]);
        break;
      case 'Products':
        this.route.navigate(['/product', id, { type: 'hire' }]);
        break;
      case 'Items':
        this.openItemDialog(
          event.option.value.id,
          event.option.value.product_id
        );
        break;
      case 'Retail Products':
        this.route.navigate(['/product/retail', id]);
        break;
    }
    this.searchInput.patchValue('');
  }

  private openItemDialog(itemId, productId) {
    this.dialog
      .open(ItemDialogComponent, {
        maxHeight: '90vh',
        data: { item: { id: itemId, product_id: productId } }
      })
      .afterClosed()
      .subscribe((doHire) => {
        if (doHire) {
          this.placeItemOnHire(itemId);
        }
      });
  }

  private placeItemOnHire(itemId: number) {
    this.dialog.open(NewTransactionDialogComponent, {
      data: { itemId, transactionProductType: 'hire' }
    });
  }

  toggleSearch() {
    this.searchVisible = !this.searchVisible;
    if (this.searchVisible) {
      setTimeout(() => {
        this.searchInputElement.nativeElement.focus();
      }, 300);
    }
  }

  openScanner() {
    this.dialog
      .open(BarcodeScannerDialogComponent)
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          if (result.type === 'item') {
            this.openItemDialog(result.resource.id, result.resource.product_id);
          }
          if (result.type === 'retailProduct') {
            this.route.navigate(['/product/retail', result.resource.id]);
          }
        }
      });
  }

  @HostListener('window:keydown', ['$event'])
  openSearch(event) {
    if (
      event.keyCode === 191 &&
      document.activeElement.tagName !== 'INPUT' &&
      document.activeElement.tagName !== 'TEXTAREA'
    ) {
      setTimeout(() => {
        this.searchInputElement.nativeElement.focus();
      }, 400);
    }
  }

  optionSelected(event) {
    if (event.option.value) {
      this.goToItem(event.option.value.id, event.option.value.type, event);
    }
  }

  ngOnDestroy() {
    this.cdr.detach();
    this.ngOnUnsubscribe.next();
  }

  setUpIdleTimeout() {
    // idle timeout of 120 minutes
    this.idle.setIdle(90 * 60);
    // sets a timeout period of 30 seconds. after 7230 seconds of inactivity, the user will be considered timed out.
    this.idle.setTimeout(60 * 5);
    // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idle.onIdleStart
      .pipe(
        takeUntil(this.ngOnUnsubscribe),
        tap(() => {
          const config = {
            disableClose: false,
            viewContainerRef: this.viewContainerRef
          } as MatDialogConfig;
          this.timeoutDialog = this.dialog.open(TimeoutDialogComponent, config);
        }),
        mergeMap(() =>
          merge(
            this.idle.onTimeout.pipe(map(() => true)),
            this.idle.onIdleEnd.pipe(map(() => false))
          )
        )
      )
      .subscribe((isTimedOut) => {
        if (this.timeoutDialog) {
          this.timeoutDialog.close();
          this.timeoutDialog = null;
        }
        if (isTimedOut) {
          this.dialog.closeAll();
          this.logOut(true);
        }
      });

    this.authenticationService.authenticated
      .pipe(takeUntil(this.ngOnUnsubscribe))
      .subscribe((auth) => {
        if (auth && !this.idle.isRunning()) {
          this.startIdleWatch();
        }
        if (!auth && this.idle.isRunning()) {
          this.stopIdleWatch();
        }
      });
  }

  stopIdleWatch() {
    this.idle.stop();
  }

  startIdleWatch() {
    this.idle.watch();
  }

  private setBodyClass() {
    const body = this.document.getElementsByTagName('body')[0];
    if (body && this.currentUser?.tenant) {
      body.className = '';
      body.classList.add(this.currentUser.tenant.tenant_ref);
    }
  }

  openUpdateDialog(): void {
    this.dialog.open(UpdateDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      disableClose: false
    });
  }
}
